/**
 * Encaminhar o CLIENTE a outro setor — o trabalhista que também tem caso de INSS.
 *
 * Não é o `Encaminhar.jsx` (aquele manda MENSAGEM para outra conversa): aqui o
 * caso nasce no setor de destino com nome, telefone e a ficha que ele entende,
 * e os dois lados ganham nota na trilha. Quem decide o que vai é o servidor
 * (`workspaces.encaminhar`); a caixa só escolhe o cliente e o destino.
 *
 * Com `record_id` a caixa já sabe o cliente (é o jeito de abrir de dentro do
 * caso); sem, ela procura na mesa do setor desta aba — é como a tela de Setores
 * a oferece enquanto o menu do caso não tem a entrada.
 */
import { useEffect, useState } from "react";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/ui/command";
import * as api from "../api.js";
import { useAcoes } from "../estado.jsx";
import { Botao, Cortina, fecharComPergunta, Seletor } from "../componentes/comuns.jsx";
import { Icone, ICONES } from "../visual.jsx";

export default function EncaminharSetor({ record_id, nome, aoFechar }) {
  const { avisar } = useAcoes();
  const [destinos, definirDestinos] = useState(null);
  const [destino, definirDestino] = useState("");
  const [cliente, definirCliente] = useState(record_id ? { id: record_id, nome } : null);
  const [termo, definirTermo] = useState("");
  const [achados, definirAchados] = useState([]);
  const [ocupado, definirOcupado] = useState(false);

  useEffect(() => {
    api.destinosDeEncaminhar().then(
      (r) => {
        definirDestinos(r.destinos ?? []);
        definirDestino((d) => d || r.destinos?.[0]?.chave || "");
      },
      (e) => {
        definirDestinos([]);
        avisar(e.message);
      },
    );
  }, [avisar]);

  /* A busca é no BANCO (a mesma da mesa), esperando a pessoa parar de digitar. */
  const q = termo.trim();
  useEffect(() => {
    if (cliente || !q) return undefined;
    const relogio = setTimeout(async () => {
      const r = await api.mesaPagina({ q, limite: 20 }).catch(() => null);
      definirAchados(r?.corpo?.atendimentos ?? []);
    }, 200);
    return () => clearTimeout(relogio);
  }, [q, cliente]);

  const mandar = async () => {
    if (!cliente || !destino) return;
    definirOcupado(true);
    try {
      const r = await api.encaminharCaso(cliente.id, destino);
      avisar(
        r.ja_existia
          ? `${cliente.nome} já era caso em ${r.destino.nome} (#${r.id}) — a nota foi para lá.`
          : `${cliente.nome} encaminhado a ${r.destino.nome} (caso #${r.id}).`,
      );
      aoFechar();
    } catch (e) {
      avisar(e.message);
    } finally {
      definirOcupado(false);
    }
  };

  /* Cliente escolhido na busca é trabalho: fechar sem encaminhar pergunta antes. */
  const sujo =
    cliente && !record_id
      ? {
          titulo: "Sair sem encaminhar?",
          texto: "O cliente escolhido se perde e nada é encaminhado.",
          acao: "Sair sem encaminhar",
        }
      : false;

  return (
    <Cortina
      aoFechar={aoFechar}
      sujo={sujo}
      largura="440px"
      antesDoTitulo={
        <span className="text-primary">
          <Icone de={ICONES.encaminhar} tamanho={18} />
        </span>
      }
      titulo="Encaminhar a outro setor"
      acima="O caso nasce no setor escolhido com o nome, o telefone e a ficha que ele entende. Os dois lados ganham nota na trilha."
      rodape={
        <>
          <Botao rotulo="Cancelar" aoTocar={fecharComPergunta(aoFechar, sujo)} />
          <Botao
            rotulo={ocupado ? "Encaminhando…" : "Encaminhar"}
            destaque
            desabilitado={!cliente || !destino || ocupado}
            aoTocar={() => void mandar()}
          />
        </>
      }
    >
      <div className="flex flex-col gap-3 p-5">
        <div>
          <div className="mb-[5px] text-apoio font-semibold text-tinta-media">Para o setor</div>
          {destinos && !destinos.length ? (
            <div className="text-corpo text-muted-foreground">Não há outro setor ativo para onde encaminhar.</div>
          ) : (
            <Seletor
              rotulo="Setor de destino"
              valor={destino}
              largura="100%"
              opcoes={(destinos ?? []).map((d) => ({ id: d.chave, rotulo: d.nome }))}
              aoEscolher={definirDestino}
            />
          )}
        </div>
        <div>
          <div className="mb-[5px] text-apoio font-semibold text-tinta-media">Cliente</div>
          {cliente ? (
            <div className="flex items-center gap-2">
              <span className="text-corpo font-semibold">{cliente.nome}</span>
              {!record_id ? (
                <button
                  type="button"
                  className="alvo text-apoio text-primary hover:underline"
                  onClick={() => definirCliente(null)}
                >
                  trocar
                </button>
              ) : null}
            </div>
          ) : (
            <Command shouldFilter={false} className="bg-transparent p-0">
              <CommandInput
                value={termo}
                onValueChange={definirTermo}
                autoFocus
                placeholder="Buscar cliente…"
                aria-label="Buscar cliente"
              />
              <CommandList className="mt-2 max-h-[240px]">
                <CommandEmpty className="py-2 text-left text-corpo text-muted-foreground">
                  {q ? "Nenhum cliente com esse nome neste setor." : "Digite o nome ou o telefone."}
                </CommandEmpty>
                {achados.map((c) => (
                  <CommandItem key={c.id} value={String(c.id)} onSelect={() => definirCliente({ id: c.id, nome: c.nome })}>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-corpo font-semibold">{c.nome}</span>
                      <span className="block text-selo text-muted-foreground">{c.etapa}</span>
                    </span>
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          )}
        </div>
      </div>
    </Cortina>
  );
}
